import React from "react";
import { Col } from "react-bootstrap";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";

const INPUT_BG = { background: "white", borderRadius: "6px" };

/**
 * ช่องกรอกข้อความ 1 ช่อง (label + TextField + error)
 * ใช้คู่กับ bind() จาก useRegisterForm:
 *   <RegisterField {...bind("nameTh", onlyThai)} label="ชื่อ (ภาษาไทย)" maxLength={50} />
 * - onChange รับ "ค่า string" (ไม่ใช่ event)
 * - onValidate(id) ถูกเรียกตอน keyUp / blur
 */
export function RegisterField({
  id,
  label,
  placeholder,
  value,
  onChange,
  feedback = "",
  onValidate,
  maxLength = 100,
  lg = 6,
  tooltip,
}) {
  return (
    <Col lg={lg}>
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <label htmlFor={id} style={{ color: "#000" }}>
          {label}
        </label>
        {tooltip && (
          <Tooltip
            title={tooltip}
            arrow
            placement="top"
            enterTouchDelay={0}
            leaveTouchDelay={4000}
          >
            <i
              className="pi pi-info-circle"
              tabIndex={0}
              aria-label={tooltip}
              style={{ color: "#053075", cursor: "help", fontSize: "14px" }}
            />
          </Tooltip>
        )}
      </div>
      <TextField
        id={id}
        value={value}
        placeholder={placeholder ?? (typeof label === "string" ? label : "")}
        size="small"
        sx={{ margin: "10px 0", fontSize: "18px" }}
        FormHelperTextProps={{ sx: { fontSize: "14px" } }}
        onChange={(e) => onChange(e.target.value)}
        {...(onValidate && {
          onKeyUp: () => onValidate(id),
          onBlur: () => onValidate(id),
        })}
        error={feedback.length !== 0}
        helperText={feedback}
        inputProps={{ sx: INPUT_BG, maxLength, tabIndex: "0" }}
        fullWidth
      />
    </Col>
  );
}

/**
 * Select แบบเลือกจาก list ของ string
 * - onChange รับ "ค่า string"
 * - onValidate(id, value) ถูกเรียกตอนเลือก (ส่งค่าใหม่ไปด้วย) และ onValidate(id) ตอน blur
 */
export function RegisterSelect({
  id,
  label,
  placeholder,
  value,
  onChange,
  feedback = "",
  onValidate,
  options,
  lg = 12,
}) {
  return (
    <Col lg={lg}>
      <FormControl fullWidth error={feedback.length !== 0}>
        <div style={{ display: "flex" }}>
          <label htmlFor={id} style={{ color: "#000" }}>
            {label}
          </label>
        </div>
        <Select
          id={`${id}-select`}
          name={`${id}-select`}
          value={value}
          displayEmpty
          renderValue={(selected) =>
            selected ? selected : <span style={{ color: "#9e9e9e" }}>{placeholder}</span>
          }
          onChange={({ target }) => {
            onValidate?.(id, target.value);
            onChange(target.value);
          }}
          onBlur={() => onValidate?.(id)}
          sx={{
            width: "100%",
            background: "white",
            margin: "10px 0",
            fontSize: "16px",
          }}
          size="small"
        >
          {options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText style={{ color: "#f44336", paddingLeft: "12px" }}>
          {feedback}
        </FormHelperText>
      </FormControl>
    </Col>
  );
}